import { createContext, useCallback, useEffect } from "react";
import axios from "utils/axios";
import { getStore } from "store/reducers";
import {
  LOGIN,
  LOGOUT,
  SET_EMAIL,
  SET_INITIALIZED,
  SET_LOADING,
  SET_ERROR,
} from "store/reducers/actions";
import { useSelector } from "react-redux";
import { authService } from "services/authService";
import LogoLoader from "components/LogoLoader";
import { processPendingReferral } from "hooks/useReferralTracking";
import { refreshSessionUserProfile } from "utils/authProfile";

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const store = getStore();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isInitialized = useSelector((state) => state.auth.isInitialized);
  const user = useSelector((state) => state.auth.user);
  const isLoading = useSelector((state) => state.auth.isLoading);

  // SEC-019: the access token is now an httpOnly cookie, never persisted to
  // localStorage or read by this code at all — so there's no client-side
  // auth state left to sync on beforeunload, no cross-tab "token changed"
  // event to listen for, and nothing to save when Redux auth state changes.
  // Cookies are already shared per-origin across tabs by the browser
  // itself, same as localStorage was.

  const restoreSessionUser = useCallback(
    async (savedUser) => {
      const user = await refreshSessionUserProfile(savedUser);
      if (!user) {
        return;
      }
      store.dispatch({
        type: LOGIN,
        payload: { user },
      });
    },
    [store],
  );

  const logout = useCallback(async () => {
    store.dispatch({ type: SET_LOADING, payload: true });
    try {
      await axios.post("/api/auth/logout/");
    } catch (error) {
      // Backend couldn't be reached or the refresh cookie was already
      // gone — still clear local state either way.
    } finally {
      store.dispatch({ type: LOGOUT });
      store.dispatch({ type: SET_LOADING, payload: false });
    }
  }, [store]);

  useEffect(() => {
    // SEC-019: no stored token to read anymore, so there's no way to know
    // "am I logged in" without asking the server — the browser already
    // sends whatever cookies it has, so just ask. /api/auth/refresh/
    // validates the refresh-token cookie (7-day life) and, on success,
    // sets a fresh access_token cookie and returns the user payload in one
    // round trip — no need to separately try the (possibly still-valid)
    // access token first.
    const initAuth = async () => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/refresh/");
        if (response.data.success && response.data.user) {
          await restoreSessionUser(response.data.user);
        } else {
          store.dispatch({ type: LOGOUT });
        }
      } catch (error) {
        store.dispatch({ type: LOGOUT });
      } finally {
        store.dispatch({ type: SET_INITIALIZED });
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    };

    initAuth();
  }, [store, restoreSessionUser]);

  useEffect(() => {
    // Register logout callback
    authService.setLogoutCallback(logout);
  }, [logout]);

  const register = async (formData) => {
    store.dispatch({ type: SET_LOADING, payload: true });
    try {
      const response = await axios.post("/api/auth/register/", formData);
      return response.data;
    } catch (error) {

      // Extract error details from the response if available
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // If we have specific error details, return them
        if (errorData.error_details) {
          // Set error in state for global handling if needed
          store.dispatch({
            type: SET_ERROR,
            payload: errorData.message || "Registration failed",
          });

          // But also return the detailed error to the component for display
          const enhancedError = new Error(
            errorData.message || "Registration failed",
          );
          enhancedError.error_details = errorData.error_details;
          throw enhancedError;
        }
      }

      // If no specific error details found, throw the original error
      throw error;
    } finally {
      store.dispatch({ type: SET_LOADING, payload: false });
    }
  };

  const login = useCallback(
    async (email, password) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/login/", {
          email,
          password,
        });
        if (response.data.success) {
          const { user } = response.data;
          // The access token arrived as an httpOnly cookie already —
          // nothing to do with it here (SEC-019).
          store.dispatch({
            type: LOGIN,
            payload: { user },
          });

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        // Check if there's an OTP required error
        if (error.response?.data?.error?.otp_required) {
          store.dispatch({
            type: SET_EMAIL,
            payload: { email },
          });
          return error.response.data;
        }
        // Check if user was created with OAuth
        else if (error.response?.data?.oauth_provider) {
          // Return the error to be handled by the UI
          const oauthError = new Error(error.response.data.error);
          oauthError.isOAuthError = true;
          oauthError.oauthProvider = error.response.data.oauth_provider;
          throw oauthError;
        }
        // Handle server error details
        else if (error.response && error.response.data) {
          const errorData = error.response.data;

          // Extract detailed error information
          const errorMessage = errorData.message || "Login failed";
          const errorDetail = errorData.detail || null;
          const specificError = errorData.error || null;

          // Set general error in the Redux store
          store.dispatch({
            type: SET_ERROR,
            payload: errorMessage,
          });

          // Throw enhanced error with all available error information
          const enhancedError = new Error(errorMessage);
          enhancedError.statusCode = error.response.status;
          enhancedError.detail = errorDetail;
          enhancedError.specificError = specificError;
          throw enhancedError;
        } else {
          // For network errors or other issues without response data
          store.dispatch({
            type: SET_ERROR,
            payload:
              "Connection failed. Please check your internet connection.",
          });
          throw error;
        }
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const verifyOTP = useCallback(
    async (otp) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const state = store.getState().auth;
        const response = await axios.post("/api/auth/verify-otp/", {
          email: state.email,
          otp,
        });

        if (response.data.success) {
          const { user } = response.data;

          // Access token arrived as an httpOnly cookie already (SEC-019).
          store.dispatch({
            type: LOGIN,
            payload: { user },
          });

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "OTP verification failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const resendOTP = useCallback(
    async (email) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/resend-otp/", {
          email,
        });

        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "OTP resend failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const resetPassword = useCallback(
    async (email) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/forgot-password/", {
          email,
        });
        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "Password reset failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const changePasswordhandle = useCallback(
    async ({ email, new_password, confirm_password, token }) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/reset-password/", {
          email,
          new_password,
          confirm_password,
          token,
        });

        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "Password change failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const updateProfile = useCallback(
    async (data) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.put("/api/account/profile", data);
        store.dispatch({
          type: LOGIN,
          payload: { user: response.data.user },
        });

        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "Profile update failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const googleOAuthLogin = useCallback(
    async (token) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/google-oauth/", {
          token,
        });

        if (response.data.success) {
          const { user } = response.data;

          // Access token arrived as an httpOnly cookie already (SEC-019).
          store.dispatch({
            type: LOGIN,
            payload: { user },
          });

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.error || "Google OAuth login failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const linkedinOAuthLogin = useCallback(
    async (code, state) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/linkedin-oauth/", {
          code,
          state,
        });

        if (response.data.success) {
          const { user } = response.data;

          // Access token arrived as an httpOnly cookie already (SEC-019).
          store.dispatch({
            type: LOGIN,
            payload: { user },
          });

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.error || "LinkedIn OAuth login failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  return (
    <JWTContext.Provider
      value={{
        login,
        logout,
        register,
        verifyOTP,
        resetPassword,
        updateProfile,
        changePasswordhandle,
        resendOTP,
        googleOAuthLogin,
        linkedinOAuthLogin,
        isLoggedIn,
        isInitialized,
        user,
        isLoading,
      }}
    >
      {isLoading ? <LogoLoader /> : children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
